import { DateTime } from 'luxon'
import cutileiApi from 'services/cutileiApi'

// Quanto da receita máxima projetada o salão quer enxergar como meta.
export const GOAL_PERCENTAGES = [50, 75, 100]

// As linhas das tabelas de vendas, na ordem da planilha. As chaves são as de `byDate[].byMethod`.
export const METHOD_ROWS = [
  { key: 'cash', label: 'Dinheiro' },
  { key: 'pix', label: 'Pix' },
  { key: 'debit', label: 'Débito' },
  ...Array.from({ length: 12 }, (_, index) => ({ key: `credit:${index + 1}`, label: `Crédito ${index + 1}x` }))
]

const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

const roundMoney = value => Math.round(value * 100) / 100

export default class CashFlow {
  static applyPercentage(value, percentage) {
    return Math.round(Number(value) * Number(percentage)) / 100
  }

  // Realizado ÷ projetado (0 a 1+); null quando não há projeção para comparar.
  static achievement(realized, projected) {
    return Number(projected) > 0 ? Number(realized) / Number(projected) : null
  }

  static sumMonths(values) {
    return Object.values(values).reduce((total, value) => total + Number(value), 0)
  }

  static async getCapacity({ businessId, startDate, endDate, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/cash_flow/capacity`, {
      ...requestAuth,
      signal,
      params: { start_date: startDate, end_date: endDate }
    })

    return data
  }

  static async getGoals({ businessId, year, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/cash_flow/goals`, {
      ...requestAuth,
      signal,
      params: { year }
    })

    return data
  }

  /*
    Total do período de cada profissional, a partir da resposta da capacidade. A API já manda, por
    dia e por profissional, o expediente em minutos, os atendimentos que cabem nele e a receita
    máxima correspondente — aqui só se soma, sem nenhuma requisição a mais.

    `days` conta os dias com expediente, não os dias com capacidade: quem tem agenda mas está sem
    serviço cadastrado trabalha os mesmos dias, só não dá para projetar nada em cima deles. É o que
    deixa a falta de cadastro visível em vez de silenciosa.

    A ordem é pela receita máxima, decrescente. Num salão com dezenas de profissionais essa é a
    pergunta que a aba responde — quem sustenta a meta. Quem está sem serviço cadastrado tem
    receita zero e cai para o fim, que é onde a linha de aviso faz sentido.
  */
  static summarizeByProfessional({ professionals, days }) {
    const round = value => Math.round(value * 100) / 100

    const summary = new Map(professionals.map(professional => [professional.id, {
      ...professional,
      days: 0,
      minutes: 0,
      capacity: 0,
      maxRevenue: 0
    }]))

    for (const day of days) {
      for (const entry of day.byProfessional) {
        const row = summary.get(entry.professionalId)

        // Profissional que saiu do salão no meio do período ainda aparece nos dias anteriores.
        if (!row) continue

        if (entry.minutes > 0) row.days += 1

        row.minutes += entry.minutes
        row.capacity += entry.capacity
        row.maxRevenue += entry.maxRevenue
      }
    }

    return [...summary.values()]
      .map(row => ({ ...row, maxRevenue: round(row.maxRevenue) }))
      .sort((first, second) => second.maxRevenue - first.maxRevenue || first.name.localeCompare(second.name))
  }

  /*
    Vencimento efetivo de uma despesa. Sem `due_date`, vale o último dia do mês de competência —
    a mesma regra que a API aplica no filtro `due_start`/`due_end` (COALESCE com LAST_DAY).
  */
  static effectiveDueDate(expense) {
    if (expense.due_date) return expense.due_date

    return DateTime.fromISO(expense.competence_month).endOf('month').toISODate()
  }

  /*
    A corrida do salão para pagar os débitos: o que está em aberto até `endDate` contra o que entra
    de recebíveis de `today` até `endDate`.

    Entram nos débitos os não pagos que já venceram (antes de `today`, de qualquer mês) e os que vencem
    até `endDate`. Os recebíveis são os de `receivables.byDate` (o líquido de taxa, no dia em que cai
    na conta) dentro do mesmo intervalo. `coverage` é a parte dos débitos que a receita cobre, e fica
    `null` quando não há débito.
  */
  static debtRace({ openExpenses, receivables, today, endDate }) {
    const open = openExpenses
      .filter(expense => !expense.paid)
      .map(expense => ({ ...expense, effective_due_date: CashFlow.effectiveDueDate(expense) }))
      .filter(expense => expense.effective_due_date <= endDate)
    const overdue = open.filter(expense => expense.effective_due_date < today)
    const upcoming = open.filter(expense => expense.effective_due_date >= today)
    const income = (receivables?.byDate ?? [])
      .filter(day => day.date >= today && day.date <= endDate)
      .reduce((total, day) => total + day.net, 0)
    const debts = CashFlow.sumValues(open)

    return {
      overdue: roundMoney(CashFlow.sumValues(overdue)),
      overdueCount: overdue.length,
      upcoming: roundMoney(CashFlow.sumValues(upcoming)),
      upcomingCount: upcoming.length,
      debts: roundMoney(debts),
      income: roundMoney(income),
      balance: roundMoney(income - debts),
      coverage: debts > 0 ? income / debts : null
    }
  }

  static sumValues(expenses) {
    return expenses.reduce((total, expense) => total + Number(expense.value), 0)
  }

  static async getPayables({ businessId, dueStart, dueEnd, paid, year, month, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/dre_expenses`, {
      ...requestAuth,
      signal,
      params: { due_start: dueStart, due_end: dueEnd, paid, year, month }
    })

    return data
  }

  // Primeiro e último dia do mês (1 a 12), em ISO, como os endpoints de período esperam.
  static monthRange(year, month) {
    const start = DateTime.local(Number(year), Number(month), 1)

    return { startDate: start.toISODate(), endDate: start.endOf('month').toISODate() }
  }

  /*
    Agrupa os recebíveis por dia (`byDate` do endpoint) em semanas de segunda a domingo dentro do
    mês. A primeira e a última semana são recortadas nas bordas do mês, e semanas sem recebível
    entram zeradas para o gráfico não pular períodos.
  */
  static groupByWeek(byDate, year, month) {
    const monthStart = DateTime.local(Number(year), Number(month), 1)
    const monthEnd = monthStart.endOf('month').startOf('day')
    const weeks = []

    for (let cursor = monthStart; cursor <= monthEnd;) {
      const end = DateTime.min(cursor.endOf('week').startOf('day'), monthEnd)

      weeks.push({
        key: cursor.toISODate(),
        start: cursor.toISODate(),
        end: end.toISODate(),
        net: 0,
        gross: 0,
        count: 0,
        byKind: { pix: 0, debit: 0, credit: 0 }
      })
      cursor = end.plus({ days: 1 })
    }

    byDate.forEach(day => {
      const week = weeks.find(item => day.date >= item.start && day.date <= item.end)

      if (!week) return

      week.net += day.net
      week.gross += day.gross
      week.count += day.count
      Object.keys(week.byKind).forEach(kind => { week.byKind[kind] += day.byKind[kind] })
    })

    const round = value => Math.round(value * 100) / 100

    return weeks.map(week => ({
      ...week,
      net: round(week.net),
      gross: round(week.gross),
      byKind: {
        pix: round(week.byKind.pix),
        debit: round(week.byKind.debit),
        credit: round(week.byKind.credit)
      }
    }))
  }

  /*
    Sem `billStart`, o período é o dos recebimentos: o que cai entre `startDate` e `endDate`. Com
    `billStart`/`billEnd`, é o das vendas: toda parcela das comandas do período, caia quando cair,
    agrupada por dia da venda e forma de pagamento (`byDate[].byMethod`).

    `byMethod` pede o mesmo agrupamento por forma de pagamento no modo dos recebimentos (por dia em
    que cai), com o dinheiro e o pix direto somados no dia da venda.
  */
  static async getReceivables({ businessId, startDate, endDate, billStart, billEnd, byMethod, requestAuth, signal }) {
    const params = billStart
      ? { bill_start: billStart, bill_end: billEnd }
      : { start_date: startDate, end_date: endDate, ...(byMethod ? { by_method: true } : {}) }

    const { data } = await cutileiApi.get(`/businesses/${businessId}/cash_flow/receivables`, {
      ...requestAuth,
      signal,
      params
    })

    return data
  }

  // Os dias de uma semana de `groupByWeek` (ou do mês inteiro), de `start` a `end`, com o rótulo "Seg 01".
  static daysOf(start, end) {
    const days = []

    for (let cursor = DateTime.fromISO(start); cursor <= DateTime.fromISO(end); cursor = cursor.plus({ days: 1 })) {
      days.push({
        date: cursor.toISODate(),
        label: `${WEEKDAY_LABELS[cursor.weekday - 1]} ${cursor.toFormat('dd')}`,
        weekday: cursor.weekday,
        weekdayLabel: WEEKDAY_LABELS[cursor.weekday - 1],
        dayLabel: `Dia ${cursor.toFormat('dd')}`
      })
    }

    return days
  }

  /*
    A tabela de uma forma de pagamento por dia, nas linhas da planilha. `field` escolhe o valor da
    célula: 'gross' (o que foi vendido) ou 'net' (o que entra na conta). Só entram as linhas com
    movimento nos dias pedidos, e o `byDate` de cada tabela é o seu: o dia da venda na de vendas, o
    dia em que o dinheiro cai na de entrada na conta.

    `rate` é a taxa efetiva do período (taxa ÷ bruto), e `share` o peso da linha no total da tabela.
  */
  static buildMethodTable({ byDate, days, field }) {
    const methodsOf = date => byDate.find(day => day.date === date)?.byMethod ?? {}
    const sumOf = (key, name) => roundMoney(days.reduce((total, day) => total + (methodsOf(day.date)[key]?.[name] ?? 0), 0))

    const rows = METHOD_ROWS
      .map(row => {
        const gross = sumOf(row.key, 'gross')

        return {
          ...row,
          gross,
          rate: gross > 0 ? sumOf(row.key, 'fee') / gross : null,
          values: days.map(day => methodsOf(day.date)[row.key]?.[field] ?? 0),
          total: sumOf(row.key, field)
        }
      })
      .filter(row => row.gross > 0)

    const total = roundMoney(rows.reduce((sum, row) => sum + row.total, 0))

    return {
      rows: rows.map(row => ({ ...row, share: total > 0 ? row.total / total : null })),
      totals: days.map((_, index) => roundMoney(rows.reduce((sum, row) => sum + row.values[index], 0))),
      total
    }
  }

  /*
    As linhas que fecham a tabela "Entra na conta", dia a dia (o `byDate` é o dos recebimentos, por
    dia em que o dinheiro cai): o que vai entrar, o que já entrou e a diferença entre os dois
    (negativa enquanto falta cair).

    `accumulated` é o quanto já terá entrado na conta ao fim de cada dia, somado desde o dia 1 do mês
    — não só dentro dos `days` pedidos, para a semana do meio do mês continuar respondendo "quanto
    terei até o dia 31". É a conta de quem precisa pagar uma despesa numa data.
  */
  static settlementRows({ byDate, days }) {
    const dayOf = date => byDate.find(day => day.date === date)
    const receivable = days.map(day => dayOf(day.date)?.net ?? 0)
    const received = days.map(day => Object.values(dayOf(day.date)?.byMethod ?? {})
      .reduce((total, method) => total + method.received, 0))

    return {
      receivable: receivable.map(roundMoney),
      received: received.map(roundMoney),
      difference: received.map((value, index) => roundMoney(value - receivable[index])),
      accumulated: days.map(day => roundMoney(byDate
        .filter(entry => entry.date <= day.date)
        .reduce((total, entry) => total + entry.net, 0)))
    }
  }

  static async getCash({ businessId, startDate, endDate, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/cash_flow/cash`, {
      ...requestAuth,
      signal,
      params: { start_date: startDate, end_date: endDate }
    })

    return data
  }

  static async getCurrentBalances({ businessId, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/bank_account_balances/current`, {
      ...requestAuth,
      signal
    })

    return data
  }

  static async getBalances({ businessId, bankAccountId, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/bank_account_balances`, {
      ...requestAuth,
      signal,
      params: { bank_account_id: bankAccountId }
    })

    return data
  }

  // Sem `balance.id` cria um lançamento; com ele, edita.
  static async saveBalance({ businessId, balance, payload, requestAuth }) {
    const url = `/businesses/${businessId}/bank_account_balances`
    const { data } = balance
      ? await cutileiApi.put(`${url}/${balance.id}`, payload, requestAuth)
      : await cutileiApi.post(url, payload, requestAuth)

    return data
  }

  static async deleteBalance({ businessId, balanceId, requestAuth }) {
    await cutileiApi.delete(`/businesses/${businessId}/bank_account_balances/${balanceId}`, requestAuth)
  }

  // O MoneyInput só entrega valores positivos; o saldo negativo (cheque especial) é um toggle.
  static signedBalance(value, negative) {
    const amount = Math.abs(Number(value) || 0)

    return negative ? -amount : amount
  }

  static async markPaid({ businessId, expenseId, paid, requestAuth }) {
    const { data } = await cutileiApi.put(
      `/businesses/${businessId}/dre_expenses/${expenseId}/paid`,
      { paid },
      requestAuth
    )

    return data
  }

  static async getDailyGoals({ businessId, startDate, endDate, requestAuth, signal }) {
    const { data } = await cutileiApi.get(`/businesses/${businessId}/cash_flow/daily_goals`, {
      ...requestAuth,
      signal,
      params: { start_date: startDate, end_date: endDate }
    })

    return data
  }

  /*
    As metas dia a dia (`daily_goals`) na forma das planilhas "Meta N°" e "Meta $": categoria →
    especialidade → profissional, e em cada nível uma célula por dia de `days` mais o total. Cada célula
    guarda a meta (com o percentual 50/75/100 aplicado) e o realizado, em quantidade, valor e comissão;
    o valor do salão é o valor menos a comissão. Os serviços da especialidade vêm somados, sem abrir
    qual foi. Cada profissional leva também o `basis`: o serviço mais lucrativo dela na especialidade e
    o R$/hora dele, que é o que a meta usa.

    Só entram especialidades e profissionais com algum movimento (meta ou realizado) nos dias pedidos.
  */
  static buildGoalSections({ data, days, percentage }) {
    const emptyCell = () => ({
      metaQuantity: 0, realizedQuantity: 0, metaValue: 0, realizedValue: 0, metaComission: 0, realizedComission: 0
    })
    const addCell = (target, source) => {
      Object.keys(target).forEach(key => { target[key] += source[key] })
    }
    const finish = cell => {
      const rounded = Object.fromEntries(Object.entries(cell).map(([key, value]) => [key, roundMoney(value)]))

      return {
        ...rounded,
        metaSalon: roundMoney(rounded.metaValue - rounded.metaComission),
        realizedSalon: roundMoney(rounded.realizedValue - rounded.realizedComission)
      }
    }
    const hasMovement = cell => Object.values(cell).some(value => value !== 0)
    const sumCells = lists => {
      const cells = days.map(emptyCell)

      lists.forEach(list => list.forEach((cell, index) => addCell(cells[index], cell)))

      return cells
    }
    const totalOf = cells => {
      const total = emptyCell()

      cells.forEach(cell => addCell(total, cell))

      return finish(total)
    }
    const dayIndex = new Map(days.map((day, index) => [day.date, index]))
    const professionalNames = new Map(data.professionals.map(professional => [professional.id, professional.name]))
    const bySpecialty = new Map()

    data.entries.forEach(entry => {
      const index = dayIndex.get(entry.date)

      if (index === undefined) return

      if (!bySpecialty.has(entry.specialtyId)) bySpecialty.set(entry.specialtyId, new Map())

      const professionals = bySpecialty.get(entry.specialtyId)

      if (!professionals.has(entry.professionalId)) professionals.set(entry.professionalId, days.map(emptyCell))

      const cell = professionals.get(entry.professionalId)[index]

      cell.metaQuantity += CashFlow.applyPercentage(entry.projected.quantity, percentage)
      cell.metaValue += CashFlow.applyPercentage(entry.projected.value, percentage)
      cell.metaComission += CashFlow.applyPercentage(entry.projected.comission, percentage)
      cell.realizedQuantity += entry.realized.quantity
      cell.realizedValue += entry.realized.value
      cell.realizedComission += entry.realized.comission
    })

    const categories = data.categories.map(category => {
      const specialties = category.specialties
        .filter(specialty => bySpecialty.has(specialty.id))
        .map(specialty => {
          const professionals = [...bySpecialty.get(specialty.id).entries()]
            .map(([id, cells]) => ({ id, name: professionalNames.get(id) ?? 'Profissional', cells }))
            .filter(professional => professional.cells.some(hasMovement))
            .sort((a, b) => a.name.localeCompare(b.name))
          const cells = sumCells(professionals.map(professional => professional.cells))

          return {
            id: specialty.id,
            name: specialty.name,
            professionals: professionals.map(professional => ({
              id: professional.id,
              name: professional.name,
              cells: professional.cells.map(finish),
              total: totalOf(professional.cells),
              basis: (data.best ?? []).find(item => (
                item.professionalId === professional.id && item.specialtyId === specialty.id
              )) ?? null
            })),
            raw: cells
          }
        })
        .filter(specialty => specialty.raw.some(hasMovement))
      const cells = sumCells(specialties.map(specialty => specialty.raw))

      return { id: category.id, name: category.name, specialties, raw: cells }
    }).filter(category => category.specialties.length > 0)

    const cells = sumCells(categories.map(category => category.raw))
    const closed = item => ({ ...item, cells: item.raw.map(finish), total: totalOf(item.raw), raw: undefined })

    return {
      categories: categories.map(category => ({ ...closed(category), specialties: category.specialties.map(closed) })),
      cells: cells.map(finish),
      total: totalOf(cells)
    }
  }
}
